import { motion } from 'framer-motion';
import { Brain, BookOpen, Scale, Heart, Target } from 'lucide-react';

const foundations = [
  {
    icon: Brain,
    title: 'Psychological Foundations',
    description: [
      'Leverages proven principles to explore unconscious patterns, identify limiting beliefs, and align emotions and behaviors for growth.',
      'Focuses on the interplay between mindset (Being), actions (Doing), and outcomes (Having), creating a holistic approach to understanding personal potential.',
    ],
  },
  {
    icon: BookOpen,
    title: 'Research-Based Framework',
    description: [
      'Built on extensive research into the characteristics of high achievers and breakthrough performers.',
      'Distills key insights into 12 actionable traits of potential, grouped into Being, Doing, and Having categories.',
    ],
  },
  {
    icon: Scale,
    title: 'The Harmonic Scale',
    description: [
      'Integrates emotional awareness by measuring emotional states across 12 levels, from lower states like Shame and Guilt to higher states like Joy and Peace.',
      'Highlights how emotional patterns influence behaviors and decisions, identifying areas for recalibration.',
    ],
  },
  {
    icon: Heart,
    title: 'Psychological and Behavioral Science',
    description: [
      'Incorporates insights from emotional intelligence, cognitive psychology, and motivation theory.',
      'Helps uncover unconscious blocks and align personal traits and emotions for greater self-awareness and achievement.',
    ],
  },
  {
    icon: Target,
    title: 'Practical Coaching Foundations',
    description: [
      'Designed for use by coaches, practitioners, and individuals, the assessment is action-oriented, offering clear strategies for personal and professional growth.',
      'Emphasizes measurable outcomes, making it a powerful tool for facilitating meaningful change.',
    ],
  },
];

export function FoundationsSection() {
  return (
    <section id="foundations" className="py-20 px-4">
      {/* Big Hero Card Section */}
      <div className="w-full mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
        >
          <div className="rounded-3xl overflow-hidden">
            <div className="flex flex-col lg:flex-row min-h-[600px]">
              {/* Image Section - Left */}
              <div className="lg:w-1/2 relative">
                <img
                  src="https://storage.googleapis.com/msgsndr/YUjCMLfw9uWPl9jO95FS/media/688751810049a54fbfe87410.jpeg"
                  alt="Transform Your Life"
                  className="w-full h-80 lg:h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-emerald-500/20"></div>
              </div>

              {/* Content Section - Right */}
              <div className="lg:w-1/2 p-6 lg:p-12 flex flex-col justify-center">
                <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  How It Works: Backed by AI, Driven by Insight
                </h2>
                <p className="text-gray-600 text-lg lg:text-xl leading-relaxed mb-8">
                  Discover your personal blueprint for transformation through a
                  powerful fusion of AI technology and human insight.
                </p>

                <div className="space-y-6 mb-8">
                  <p className="text-gray-600 text-lg leading-relaxed">
                    The Personality Breakthrough Profile™ is more than just a
                    test — it's a coaching experience built for real change.
                  </p>

                  <p className="text-gray-600 text-lg leading-relaxed">
                    Using a science-grounded framework of 12 personality traits
                    and 12 emotional levels, the platform analyzes your
                    responses and maps out your dominant patterns, inner
                    drivers, and hidden growth blocks.
                  </p>

                  <p className="text-gray-600 text-lg leading-relaxed">
                    Then, powered by Human Insights AI, your results are brought
                    to life with intelligent, personalized feedback — offering
                    clarity, strategy, and momentum in minutes.
                  </p>

                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    Whether you're a coach guiding others or on your own growth
                    journey, this platform helps you:
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
                    <span className="text-gray-700 font-medium text-base lg:text-lg">
                      Identify your most critical Breakthrough Zone™
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                    <span className="text-gray-700 font-medium text-base lg:text-lg">
                      Understand how your traits and emotions work together
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                    <span className="text-gray-700 font-medium text-base lg:text-lg">
                      Get coaching-style insights on exactly what to shift next
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                    <span className="text-gray-700 font-medium text-base lg:text-lg">
                      Use it to spark deeper self-awareness or real client
                      breakthroughs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="max-w-none mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Foundations of The Personality Breakthrough Profile
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Built on a unique, integrative framework that combines:
          </p>
        </motion.div>

        {/* Original Foundations Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {foundations.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 hover:scale-[1.02] min-h-[320px]"
            >
              <div className="text-center">
                <div
                  className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 ${
                    index === 0
                      ? 'bg-blue-100'
                      : index === 1
                      ? 'bg-emerald-100'
                      : index === 2
                      ? 'bg-purple-100'
                      : index === 3
                      ? 'bg-rose-100'
                      : 'bg-orange-100'
                  }`}
                >
                  <item.icon
                    className={`w-8 h-8 ${
                      index === 0
                        ? 'text-blue-600'
                        : index === 1
                        ? 'text-emerald-600'
                        : index === 2
                        ? 'text-purple-600'
                        : index === 3
                        ? 'text-rose-600'
                        : 'text-orange-600'
                    }`}
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {item.title}
                </h3>
                <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
                  {item.description.map((text, i) => (
                    <p key={i}>{text}</p>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
